import { FeatureFlagsRulesServiceBase, HttpService, LocalContext, ObservableCollection, QueryBus, Repository, RepositoryBase, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { AddressModel, DocumentPaymentListItemModel, ExchangeRateModel, RelatedEntity, SaleAdvanceAdjustmentDocumentModel, SaleAdvanceDocumentItemModel, SaleGetDocumentDTO, SaleUpdateDocumentDTO, VatTableItemModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel, documentDeliveryAddressFilled, serializeAddressModel, serializeDocumentBuyerModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentItemModel, serializeDocumentPaymentListItemModel } from '../common/serialziation-utils';
import { ProductJpkVatGroupCollectionQuery } from '../product/queries/product-jpk-vat-group.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { SaleJpkVatGroupCollectionQuery } from './queries/sale-jpk-vat-group.query-base';
import { SaleAdvanceAdjustmentBO } from './sale-advance-adjustment.bo';


@Repository({
    boType: SaleAdvanceAdjustmentBO,
    apiController: TaggedUrl.join('sales', [Tags.Api]),
    dto: {
        get: SaleGetDocumentDTO,
        update: SaleUpdateDocumentDTO
    }
})
export class SaleAdvanceAdjustmentRepositoryBase extends RepositoryBase<SaleAdvanceAdjustmentBO, SaleAdvanceAdjustmentDocumentModel, SaleGetDocumentDTO, SaleUpdateDocumentDTO> {

    public constructor(
        http: HttpService,
        private readonly weaver: Weaver,
        private readonly clientRepository: ClientRepositoryBase,
        private readonly productRepository: ProductRepositoryContract,
        private readonly queryBus: QueryBus,
        private readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase
    ) {
        super(http);
    }

    protected createBO(): SaleAdvanceAdjustmentBO {
        return new SaleAdvanceAdjustmentBO(this.weaver, this.clientRepository, this.featureFlagsRulesService, this.queryBus, this.productRepository);
    }

    public async fromPlainObjectAsync(item: SaleGetDocumentDTO): Promise<SaleAdvanceAdjustmentDocumentModel> {
        const document = new SaleAdvanceAdjustmentDocumentModel(pickBy<Partial<SaleAdvanceAdjustmentDocumentModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,

            relatedInventoryDocuments: item.relatedInventoryDocuments?.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            includesInventoryControl: item.includesInventoryControl,

            advanceReturnValue: item.advanceReturnValue,
            adjustedDocumentId: item.adjustedDocumentId,

            adjustmentReasonId: item.adjustmentReasonId,
            bankAccountNumberId: item.bankAccountId,
            baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
            calculationMethod: item.calculationMethod,
            cashBasis: item.cashBasis,
            communityTransactionId: item.communityTransactionId,
            currencyId: item.currencyId,
            documentNumber: item.documentNumber,
            dueDate: item.dueDate,
            dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
            favourite: item.favourite,
            grossValue: item.totalGrossValue,
            issueDate: item.issueDate,
            issuedByVatPayer: item.issuedByVatPayer,
            issuer: item.issuer,
            jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
            kind: item.kind,
            marginSchemeId: item.marginSchemeId,
            netValue: item.totalNetValue,
            note: item.note,
            numberingId: item.numberingId,
            paymentMethodId: item.paymentMethodId,
            recipient: item.recipient,
            saleDate: item.deliveryDate,
            splitPayment: item.splitPayment,
            taxLiabilityDate: item.taxLiabilityDate,
            transactionLocation: item.transactionLocation,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonSource: item.vatReleaseReasonSource,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoAdditionalInformation: item.wstoAdditionalInformation,
            customDueDateDescription: item.customDueDateDescription,
            ksefProcessingStage: item.ksefProcessingStage,
            ksefDocumentStatus: item.ksefDocumentStatus,
            ksefDocumentId: item.ksefDocumentId,
            ksefCollectiveIdentifier: item.ksefCollectiveIdentifier,
            ksefAcquisitionDateTime: item.ksefAcquisitionDateTime,
            ksefLastStatusCheckDate: item.ksefLastStatusCheckDate,
            ksefIssueDate: item.ksefIssueDate,
            ksefSessionReferenceNumber: item.ksefSessionReferenceNumber,
            ksefLastReceivingUpoDate: item.ksefLastReceivingUpoDate,
            ksefSessionState: item.ksefSessionState,
            ksefSessionEndDate: item.ksefSessionEndDate,
            ksefHash: item.ksefHash,
            ksefUpoDownloadUrl: item.ksefUpoDownloadUrl,
            ksefUpoDownloadUrlExpirationDate: item.ksefUpoDownloadUrlExpirationDate,
            ksefSubmissionMode: item.ksefSubmissionMode,
            ksefCertificateUrl: item.ksefCertificateUrl,
            ksefXmlExported: item.ksefXmlExported,
            ksefXmlImported: item.ksefXmlImported,
            ksefXmlUrl: item.ksefXmlUrl,
            orderNumber: item.additionalData?.orderNumber,
            orderDate: item.additionalData?.orderDate,
            agreementNumber: item.additionalData?.agreementNumber,
            agreementDate: item.additionalData?.agreementDate,
            rateNP: item.rateNP,
            reverseCharge: item.reverseCharge
        }, val => val !== void 0));

        document.client = deserializeDocumentClientModel(item.client);
        document.buyer = deserializeDocumentBuyerModel(item.buyer);

        document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
            name: item.deliveryAddress.name,
            zipCode: item.deliveryAddress.zipCode,
            line1: item.deliveryAddress.address,
            city: item.deliveryAddress.city,
            countryId: item.deliveryAddress.countryId
        }, val => val !== void 0)) : null as any;

        document.items = new ObservableCollection(item.items.map(documentItem => {
            return new SaleAdvanceDocumentItemModel(pickBy<Partial<SaleAdvanceDocumentItemModel>>({
                advance: documentItem.advance,
                cnCode: documentItem.cnCode,
                cost: documentItem.cost,
                ordinalNumber: documentItem.no,
                discountKind: documentItem.discountKind,
                discountValue: documentItem.discountValue,
                jpkVatGroups: documentItem.jpkVatGroups,
                jpkVatProcedure: documentItem.jpkVatProcedure,
                relatedInventoryItemsIds: documentItem.relatedInventoryItemsIds || [],
                includesInventoryControl: documentItem.includesInventoryControl,
                note: documentItem.note,
                price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                price_grossBeforeDiscount: documentItem.unitGrossPrice,
                price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                price_netBeforeDiscount: documentItem.unitNetPrice,
                id: documentItem.id,
                productId: documentItem.productId,
                productName: documentItem.productName,
                pkwiu: documentItem.pkwiu,
                symbol: documentItem.symbol,
                barCode: documentItem.barCode,
                quantity: documentItem.quantity,
                unitCost: documentItem.unitCost,
                unitId: documentItem.unitId,
                value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                value_grossBeforeDiscount: documentItem.grossValue,
                value_netAfterDiscount: documentItem.netValueAfterDiscount,
                value_netBeforeDiscount: documentItem.netValue,
                value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                value_vatBeforeDiscount: documentItem.vatValue,
                vatRateId: documentItem.vatRateId
            }, val => val !== void 0));
        }));

        if (item.exchangeRate) {
            document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                rate: item.exchangeRate.rate,
                units: item.exchangeRate.units,
                manual: item.exchangeRate.manual,
                tableNo: item.exchangeRate.tableNo,
            }, val => val !== void 0));
        }

        document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.currencyGross ?? item.gross,
                netValue: item.currencyNet ?? item.net,
                vatRateId: item.vatRateId,
                vatValue: item.currencyVat ?? item.vat,
            }, val => val !== void 0));
        }));

        document.vatTableItemsInBaseCurrency = new ObservableCollection(item.vatTable.items.map(item => {
            return item.currencyGross != null ? new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.gross,
                netValue: item.net,
                vatRateId: item.vatRateId,
                vatValue: item.vat
            }, val => val !== void 0)) : null;
        }).filter(item => item !== null));

        document.payments = new ObservableCollection(item.payments.map(item => {
            return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                id: item.id,
                date: item.date,
                value: item.amount,
                onDocument: item.onDocument
            }, val => val !== void 0));
        }));

        return document;
    }

    protected async preparePlainObjectAsync(model: SaleAdvanceAdjustmentDocumentModel, ctx: LocalContext): Promise<SaleUpdateDocumentDTO> {
        const jpkVatDocumentGroupCodes = await ctx.getAsync(SaleJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));
        const jpkVatProductGroupCodes = await ctx.getAsync(ProductJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));

        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,

            advanceReturnValue: model.advanceReturnValue,

            relatedInventoryDocuments: model.relatedInventoryDocuments?.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            firstAdvanceDocumentId: model.firstAdvanceDocumentId,
            firstAdvanceDocumentVersion: model.firstAdvanceDocumentVersion,
            adjustedDocumentId: model.adjustedDocumentId,

            adjustmentReasonId: model.adjustmentReasonId,
            bankAccountId: model.bankAccountNumberId as any,
            baseCurrencyBankAccountId: model.baseCurrencyBankAccountNumberId as any,
            buyer: model.buyer ? serializeDocumentBuyerModel(model.buyer) : null as any,
            calculationMethod: model.calculationMethod,
            client: model.client ? serializeDocumentClientModel(model.client) : null as any,
            cashBasis: model.cashBasis,
            communityTransactionId: model.communityTransactionId,
            currencyId: model.currencyId,
            deliveryAddress: documentDeliveryAddressFilled(model.deliveryAddress) ? serializeAddressModel(model.deliveryAddress) : null as any,
            deliveryDate: getStartOfDate(model.saleDate!),
            documentNumber: model.documentNumber,
            dueDate: getStartOfDate(model.dueDate),
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null as any,
            favourite: model.favourite,
            goodsIssueDate: null,
            issueDate: getStartOfDate(model.issueDate),
            issuedByVatPayer: model.issuedByVatPayer,
            issuer: model.issuer,
            items: model.items.map(serializeDocumentItemModel),
            jpkVatDocumentGroups: model.jpkVatGroups.filter(g => jpkVatDocumentGroupCodes.includes(g)),
            jpkVatProductGroups: model.jpkVatGroups.filter(g => jpkVatProductGroupCodes.includes(g)),
            kind: model.kind,
            marginSchemeId: model.marginSchemeId,
            note: model.note,
            numberingId: model.numberingId,
            payments: model.payments.map(serializeDocumentPaymentListItemModel),
            paymentMethodId: model.paymentMethodId,
            recipient: model.recipient,
            splitPayment: model.splitPayment,
            taxLiabilityDate: getStartOfDate(model.taxLiabilityDate),
            transactionLocation: model.transactionLocation,
            wstoShipmentCountryId: model.wstoShipmentCountryId,
            wstoConsumerCountryId: model.wstoConsumerCountryId,
            wstoAdditionalInformation: model.wstoAdditionalInformation,
            customDueDateDescription: model.customDueDateDescription,
            vatReleaseReasonId: model.vatReleaseReasonId,
            vatReleaseReasonCustomName: model.vatReleaseReasonCustomName as any,
            vatReleaseReasonSource: model.vatReleaseReasonSource,
            ksefSubmissionMode: model.ksefSubmissionMode,
            additionalData: {
                orderNumber: model.orderNumber,
                orderDate: model.orderDate ? getStartOfDate(model.orderDate) : null,
                agreementNumber: model.agreementNumber,
                agreementDate: model.agreementDate ? getStartOfDate(model.agreementDate) : null
            },
            rateNP: model.rateNP,
            reverseCharge: model.reverseCharge,
            adjustPaymentDate: false,
        };
    }
}
